import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { Card, Row, Col, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ProfileContext } from "pages/Profile";
import { hasMemberNumber, isOfficer } from "utils/acl";

export default function MyToastmasters() {
  const { profile, setProfile } = useContext(ProfileContext);
  const { user: authUser } = useSelector((x) => x.auth);

  const url = process.env.REACT_APP_URL;

  const [fields, setFields] = useState({
    memberNo: '',
    acl: 'Guest',
    paidUntil: '',
    clubRoles: [],
  });
  const [editingField, setEditingField] = useState(null);
  const [isSavingField, setIsSavingField] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleToAdd, setRoleToAdd] = useState('');
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [roleOptionsError, setRoleOptionsError] = useState('');
  const [notifNextMeeting, setNotifNextMeeting] = useState(false);
  const [notifMeetingRoleAssignment, setNotifMeetingRoleAssignment] = useState(false);
  const [notifNewsletter, setNotifNewsletter] = useState(false);

  useEffect(() => {
    setFields({
      memberNo: profile?.memberNo || profile?.toastmastersId || '',
      acl: profile?.acl || 'Guest',
      paidUntil: profile?.paidUntil || '',
      clubRoles: Array.isArray(profile?.clubRoles) ? profile.clubRoles : [],
    });
    setNotifNextMeeting(profile?.notifNextMeeting || false);
    setNotifMeetingRoleAssignment(profile?.notifMeetingRoleAssignment || false);
    setNotifNewsletter(profile?.notifNewsletter || false);
  }, [profile]);

  useEffect(() => {
    let active = true;

    const loadOfficerRoles = async () => {
      setIsLoadingRoles(true);
      setRoleOptionsError('');
      try {
        const response = await fetch(`${url}/officer-role-catalog`, {
          headers: { 'Authorization': `Bearer ${authUser.token}` },
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result?.message || 'Failed to load officer roles');
        }

        if (!active) return;
        const roles = Array.isArray(result?.roles) ? result.roles : [];
        setRoleOptions(roles);
        setRoleToAdd((current) => roles.includes(current) ? current : (roles[0] || ''));
      } catch (error) {
        if (!active) return;
        setRoleOptions([]);
        setRoleToAdd('');
        setRoleOptionsError(error.message || 'Failed to load officer roles');
      } finally {
        if (active) setIsLoadingRoles(false);
      }
    };

    loadOfficerRoles();
    return () => {
      active = false;
    };
  }, [url, authUser.token]);

  const canEdit = useMemo(
    () => authUser.user.acl === 'Officer' || authUser.user._id === profile._id,
    [authUser.user.acl, authUser.user._id, profile._id]
  );

  const canEditPaidUntil = useMemo(
    () => isOfficer(authUser?.user?.acl) || Boolean(authUser?.user?.isWebmaster),
    [authUser?.user?.acl, authUser?.user?.isWebmaster]
  );

  const paidUntilOptions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const startYear = currentYear - 3;
    const maxDate = new Date(now.getFullYear() + 2, now.getMonth(), now.getDate());

    const options = [];
    for (let year = startYear; year <= currentYear + 3; year++) {
      const march31 = new Date(year, 2, 31);
      const sept30 = new Date(year, 8, 30);

      if (march31 <= maxDate) {
        options.push(`Mar 31, ${year}`);
      }
      if (sept30 <= maxDate) {
        options.push(`Sep 30, ${year}`);
      }
    }
    if (fields.paidUntil && !options.includes(fields.paidUntil)) {
      options.unshift(fields.paidUntil);
    }
    return options;
  }, [fields.paidUntil]);

  const updateField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const persistToastmastersDetails = useCallback(async (partial = null) => {
    if (!canEdit && !canEditPaidUntil) return;

    const nextFields = partial ? { ...fields, ...partial } : fields;
    setIsSavingField(true);
    try {
      const data = {
        memberNo: nextFields.memberNo,
        acl: nextFields.acl,
        paidUntil: nextFields.paidUntil,
        clubRoles: Array.isArray(nextFields.clubRoles) ? nextFields.clubRoles : [],
      };

      const response = await fetch(`${url}/users/${profile._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.Error || 'Failed to update Toastmasters details');
      }
      setProfile(prev => ({ ...prev, ...result, avatarUrl: prev?.avatarUrl || result?.avatarUrl || null, avatar: prev?.avatar || result?.avatar || null }));
      toast.success('Toastmasters details have been updated', { position: 'bottom-left' });
    } catch (error) {
      toast.error('Failed to update Toastmasters details', { position: 'bottom-left' });
      console.error(error);
    } finally {
      setIsSavingField(false);
    }
  }, [canEdit, canEditPaidUntil, fields, url, profile._id, authUser.token, setProfile]);

  const startEditing = (fieldKey) => {
    if (fieldKey === 'paidUntil') {
      if (!canEditPaidUntil || isSavingField) return;
    } else if (!canEdit || isSavingField) {
      return;
    }
    if (fieldKey === 'clubRoles') {
      const available = roleOptions.find((role) => !fields.clubRoles.includes(role));
      setRoleToAdd(available || '');
    }
    setEditingField(fieldKey);
  };

  const finishEditing = async () => {
    if (!editingField || isSavingField) return;
    await persistToastmastersDetails();
    setEditingField(null);
  };

  const cancelEditing = () => {
    setFields({
      memberNo: profile?.memberNo || profile?.toastmastersId || '',
      acl: profile?.acl || 'Guest',
      paidUntil: profile?.paidUntil || '',
      clubRoles: Array.isArray(profile?.clubRoles) ? profile.clubRoles : [],
    });
    setEditingField(null);
  };

  const handleAddRole = async () => {
    if (!roleToAdd || fields.clubRoles.includes(roleToAdd)) return;
    const nextRoles = [...fields.clubRoles, roleToAdd];
    setFields((prev) => ({ ...prev, clubRoles: nextRoles }));
    await persistToastmastersDetails({ clubRoles: nextRoles });
    setRoleToAdd(roleOptions.find((role) => !nextRoles.includes(role)) || '');
  };

  const handleRemoveRole = async (role) => {
    const nextRoles = fields.clubRoles.filter((entry) => entry !== role);
    setFields((prev) => ({ ...prev, clubRoles: nextRoles }));
    await persistToastmastersDetails({ clubRoles: nextRoles });
  };

  const persistNotifications = useCallback(async (data) => {
    if (!canEdit) return;
    setIsSavingNotifications(true);
    try {
      const response = await fetch(`${url}/users/${profile._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.Error || 'Failed to update notifications');
      }
      setProfile(prev => ({ ...prev, ...result, avatarUrl: prev?.avatarUrl || result?.avatarUrl || null, avatar: prev?.avatar || result?.avatar || null }));
    } catch (error) {
      toast.error('Failed to update notifications', { position: 'bottom-left' });
      console.error(error);
    } finally {
      setIsSavingNotifications(false);
    }
  }, [canEdit, url, profile._id, authUser.token, setProfile]);

  const handleNotifNextMeetingChange = useCallback((checked) => {
    setNotifNextMeeting(checked);
    persistNotifications({
      notifNextMeeting: checked,
      notifMeetingRoleAssignment,
      notifNewsletter,
    });
  }, [persistNotifications, notifMeetingRoleAssignment, notifNewsletter]);

  const handleNotifMeetingRoleAssignmentChange = useCallback((checked) => {
    setNotifMeetingRoleAssignment(checked);
    persistNotifications({
      notifNextMeeting,
      notifMeetingRoleAssignment: checked,
      notifNewsletter,
    });
  }, [persistNotifications, notifNextMeeting, notifNewsletter]);

  const handleNotifNewsletterChange = useCallback((checked) => {
    setNotifNewsletter(checked);
    persistNotifications({
      notifNextMeeting,
      notifMeetingRoleAssignment,
      notifNewsletter: checked,
    });
  }, [persistNotifications, notifNextMeeting, notifMeetingRoleAssignment]);

  const renderEditableRow = ({ key, label, value, type = 'text', placeholder = '' }) => {
    const isEditing = editingField === key;

    return (
      <div className="settings-inline-row" key={key}>
        <span className="settings-inline-label">{label}</span>
        {isEditing ? (
          <Form.Control
            autoFocus
            type={type}
            className="settings-inline-input"
            value={value}
            placeholder={placeholder}
            onChange={(e) => updateField(key, e.target.value)}
            onBlur={finishEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                finishEditing();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditing();
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="settings-inline-value"
            onClick={() => startEditing(key)}
            disabled={!canEdit || isSavingField}
          >
            {value || 'N/A'}
          </button>
        )}
      </div>
    );
  };

  return (
    <Card className="mt-3 mb-3 user-card settings-card meetings-mock-card-shell meetings-page-card">
      <Card.Header className="settings-card-header">
        <Row className="mt-2 mb-2"><Col>My Toastmasters</Col></Row>
      </Card.Header>

      <Card.Body className="settings-card-body">
        <div className="settings-inline-list">
          {hasMemberNumber(fields.acl) && (
            renderEditableRow({ key: 'memberNo', label: 'Member Number', value: fields.memberNo, placeholder: 'Toastmasters Membership Number' })
          )}

          {authUser.user.acl === 'Officer' && (
            <div className="settings-inline-row">
              <span className="settings-inline-label">Membership</span>
              {editingField === 'acl' ? (
                <Form.Select
                  autoFocus
                  className="settings-inline-input"
                  value={fields.acl}
                  onChange={(e) => updateField('acl', e.target.value)}
                  onBlur={finishEditing}
                >
                  <option value='Guest'>Guest</option>
                  <option value='Member'>Member</option>
                  <option value='Guest Member'>Guest Member</option>
                  <option value='Officer'>Officer</option>
                </Form.Select>
              ) : (
                <button
                  type="button"
                  className="settings-inline-value"
                  onClick={() => startEditing('acl')}
                  disabled={!canEdit || isSavingField}
                >
                  {fields.acl || 'Guest'}
                </button>
              )}
            </div>
          )}

          <div className="settings-inline-row">
            <span className="settings-inline-label">Paid until</span>
            {editingField === 'paidUntil' ? (
              <Form.Select
                autoFocus
                className="settings-inline-input"
                value={fields.paidUntil || ''}
                onChange={(e) => updateField('paidUntil', e.target.value)}
                onBlur={finishEditing}
              >
                <option value="">Unset</option>
                {paidUntilOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </Form.Select>
            ) : (
              <button
                type="button"
                className="settings-inline-value"
                onClick={() => startEditing('paidUntil')}
                disabled={!canEditPaidUntil || isSavingField}
              >
                {fields.paidUntil || 'N/A'}
              </button>
            )}
          </div>

          {fields.acl === 'Officer' && (
            <div className="settings-inline-row">
              <span className="settings-inline-label">Toastmasters Roles</span>

              {editingField === 'clubRoles' ? (
                <div className="settings-roles-editor">
                  <div className="settings-roles-controls">
                    <Form.Select
                      className="settings-inline-input settings-inline-input--short"
                      value={roleToAdd}
                      onChange={(e) => setRoleToAdd(e.target.value)}
                      disabled={isLoadingRoles || roleOptions.length === 0}
                    >
                      {isLoadingRoles && <option value="">Loading roles...</option>}
                      {!isLoadingRoles && roleOptions.length === 0 && <option value="">No roles available</option>}
                      {roleOptions.map((role) => (
                        <option key={role} value={role} disabled={fields.clubRoles.includes(role)}>{role}</option>
                      ))}
                    </Form.Select>
                    <button type="button" className="settings-role-add-btn" onClick={handleAddRole} disabled={!roleToAdd || isSavingField}>
                      Add
                    </button>
                    <button type="button" className="settings-role-done-btn" onClick={() => setEditingField(null)}>
                      Done
                    </button>
                  </div>

                  {roleOptionsError && <span className="settings-role-empty">{roleOptionsError}</span>}

                  <div className="settings-role-chips">
                    {fields.clubRoles.length === 0 && <span className="settings-role-empty">No roles selected.</span>}
                    {fields.clubRoles.map((role) => (
                      <span key={role} className="settings-role-chip">
                        {role}
                        <button type="button" className="settings-role-chip-remove" onClick={() => handleRemoveRole(role)} aria-label={`Remove ${role}`}>
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="settings-inline-value"
                  onClick={() => startEditing('clubRoles')}
                  disabled={!canEdit || isSavingField}
                >
                  {fields.clubRoles.length > 0 ? fields.clubRoles.join(', ') : 'N/A'}
                </button>
              )}
            </div>
          )}

          <p className="mb-2">Notify me for:</p>

          <div className="settings-inline-row">
            <Form.Check
              disabled={!canEdit || isSavingNotifications}
              type="switch"
              label="Next Meeting"
              checked={notifNextMeeting}
              onChange={(e) => handleNotifNextMeetingChange(e.target.checked)}
            />
          </div>

          <div className="settings-inline-row">
            <Form.Check
              disabled={!canEdit || isSavingNotifications}
              type="switch"
              label="Meeting Role Assignment"
              checked={notifMeetingRoleAssignment}
              onChange={(e) => handleNotifMeetingRoleAssignmentChange(e.target.checked)}
            />
          </div>

          <div className="settings-inline-row">
            <Form.Check
              disabled={!canEdit || isSavingNotifications}
              type="switch"
              label="Newsletter"
              checked={notifNewsletter}
              onChange={(e) => handleNotifNewsletterChange(e.target.checked)}
            />
          </div>
        </div>
      </Card.Body>

      {(isSavingField || isSavingNotifications) && (
        <Card.Footer className="settings-card-footer">
          <Row className="mt-2 mb-2"><Col className="text-end">Saving profile...</Col></Row>
        </Card.Footer>
      )}
    </Card>
  );
}
